import moment from 'moment';
import { isObject, get, includes, isEqual, isEmpty, isUndefined } from 'lodash';
import React from 'react';

import ConfirmationDialog from '../../common/components/Form/ConfirmationDialog';
import { Snackbar } from '../../common/components/Snackbar/Snackbar';
import ChargerFormBuilidingService from '../../common/services/ChargerFormBuilidingService';
import hasAccess, { editChargerDetailsAccessId, viewChargerConfigsId } from '../../common/services/HasAccess';
import { iotOnDateFormat } from '../../common/services/ValidationRules';
import ChargerDetailsForm from './components/ChargerDetailsForm';
import { CHARGER_STATUS_PENDING, SYSTEM_GENERATED_FIELDS } from '../../common/constants/statuses';

class ChargerDetails extends React.PureComponent {
  state = {
    mode: null,
    tabsContent: [],
    timestamp: null,
    currentChargerData: null,
    openConfirmationDialog: false,
    acceptedConfirmationDialog: false,
    autoGeneratedConfirmationDialog: false,
    formData: null,
    formMode: 'view',
    activeTab: this.props.activeTab || 'details',
    sourceIsDashboard: window.location.href.includes('dashboard'),
    showExitDialog: false,
    mobileMenuOpened: false,
  };

  constructor(props) {
    super(props);

    this.scrollToFirstError = this.scrollToFirstError.bind(this);
    this.submitAutoGeneratedDialog = this.submitAutoGeneratedDialog.bind(this);
    this.closeAutoGeneratedDialog = this.closeAutoGeneratedDialog.bind(this);
  }

  componentDidMount() {
    const currentTab = get(this.props, 'match.params.tab');
    const { location, navigate, configuration, unlockConfig } = this.props;

    if (!currentTab && navigate && location) {
      navigate(`${location.pathname}/details`);
    }

    this.props.toggleReinitForm(false);
    this.props.loadAllDictionaries({ status: 'ACTIVE' });
    this.handleCloseSnackBar();

    const id = this.props.chargerId || this.props.match.params.chargerId;

    this.props.loadCharger(id);
    if (hasAccess(viewChargerConfigsId, get(this.props.user, 'currentPermissions'), this.props.config)) {
      this.props.loadChargerConfiguration(id);
    }

    if (!isEmpty(configuration.nodes)) {
      unlockConfig(configuration.chargerUuid);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      detailsForm: { values: nextValues },
      reinitForm: nextReinitForm,
      user: { currentPermissions: nextPermissions },
    } = nextProps;
    const {
      detailsForm: { values: prevValues },
      reinitForm: prevReinitForm,
      toggleReinitForm,
      user: { currentPermissions },
    } = this.props;

    if (nextReinitForm && prevReinitForm !== nextReinitForm) {
      this.initTabs(nextProps);
      toggleReinitForm(false);
    } else {
      if (
        nextValues &&
        prevValues &&
        (get(nextValues, 'i2p2_state.c_condition') !== get(prevValues, 'i2p2_state.c_condition') ||
          !isEqual(currentPermissions, nextPermissions))
      ) {
        this.initTabs(nextProps);
      }
    }
  }

  initTabs({ modal, currentCharger, user: { currentPermissions, customer_id }, detailsForm: { values: currentChargerData }, config }) {
    const formMode = this.getFormMode(modal, currentCharger, customer_id, currentPermissions);
    const tabsContent = ChargerFormBuilidingService.buildTabsWithMode(formMode, currentChargerData, config, currentPermissions);
    const updateTimestamp = get(currentChargerData, 'update_reason_timestamp', '');
    const updateReason = get(currentChargerData, 'update_reason', '');
    const timeToShow = updateTimestamp ? moment.utc(updateTimestamp).format(iotOnDateFormat) : '';

    this.setState({
      updateTimestamp: timeToShow,
      updateReason,
      tabsContent,
      currentChargerData,
      formMode,
    });
  }

  getFormMode(modal, currentCharger, customer_id, permissions) {
    if (!modal && currentCharger && customer_id === get(currentCharger, 'customer.customerId')) {
      const pathname = window.location.pathname;

      if (pathname.includes('/edit') && hasAccess(editChargerDetailsAccessId, permissions)) {
        const condition = get(currentCharger, 'i2p2_state.c_condition');

        if (condition === CHARGER_STATUS_PENDING) {
          return 'create';
        }

        return 'edit';
      }
    }

    return 'view';
  }

  convertErrorsObj(errors, path = '') {
    if (errors instanceof Array) {
      return path;
    }

    const firstKey = Object.keys(errors)[0];
    const val = errors[firstKey];

    if (val && isObject(val)) {
      const pathFinal = path ? `${path}.${firstKey}` : firstKey;

      return this.convertErrorsObj(val, pathFinal);
    }

    return path ? `${path}.${firstKey}` : path;
  }

  scrollToField = (fieldName) => {
    const failElement = document.querySelector(`[data-field-name="${fieldName}"]`);

    if (failElement) {
      failElement.firstChild.scrollIntoView({ behavior: 'smooth' });
    }
  };

  scrollToFirstError = (errors) => {
    const firstErrorField = this.convertErrorsObj(errors, null);

    this.scrollToField(firstErrorField);

    tabs: for (const tabObj of this.state.tabsContent) {
      if (tabObj.type === 'tab') {
        for (const field of tabObj.fields) {
          if (field.name === firstErrorField) {
            this.props.setActiveTab(tabObj.value, this.props.navigate);
            break tabs;
          }
        }
      }

      if (tabObj.type === 'sections') {
        for (const section of tabObj.sections) {
          if (section.fields.length) {
            for (const sectionField of section.fields) {
              if (sectionField.name.indexOf(firstErrorField) > -1) {
                this.props.setActiveTab(tabObj.value, this.props.navigate);
                break tabs;
              }
            }
          }
        }
      }
    }
  };

  onClose = () => {
    if (this.state.sourceIsDashboard) {
      this.props.navigate('/dashboard/list-view');
    } else {
      this.props.navigate('/chargers/chargers_list');
    }
  };

  backButtonHandler = () => {
    const isFirstCustomerRedirect = this.props.location.key === 'default';

    isFirstCustomerRedirect ? this.props.navigate('/') : this.props.navigate(-1);
  };

  submitForm(data) {
    return this.props.updateChargerBody(data);
  }

  onSubmit = (data) => {
    this.setState({ showExitDialog: false });
    const { formPristine, formValues, initialFormValues } = this.props;

    const systemGeneratedValues = get(formValues, 'i2p2_state.system_generated_values', []);
    const untouchedGeneratedFields = SYSTEM_GENERATED_FIELDS.filter(field => systemGeneratedValues.includes(field.key) && get(formValues, field.path) === get(initialFormValues, field.path)).map(field => field.key)

    if (
      !formPristine &&
      formValues.i2p2_state.c_condition === 'inactive' &&
      formValues.i2p2_state.c_condition !== initialFormValues.i2p2_state.c_condition &&
      !this.state.acceptedConfirmationDialog
    ) {
      this.setState({ openConfirmationDialog: true, formData: data });
    } else {
      data.i2p2_state.system_generated_values = untouchedGeneratedFields

      this.setState({ acceptedConfirmationDialog: false, formData: data });

      if (untouchedGeneratedFields.length > 0) {
        this.setState({
          autoGeneratedConfirmationDialog: true,
        })
      } else {
        return this.submitForm(data);
      }
    }
  };

  selectTab = (value) => {
    const demo = window.location.search.includes('demo');

    if (includes(['telemetry', 'telemetry/realtime'], value) && demo) {
      value = `${value}?demo`;
    }
    this.setState({ activeTab: value });
    this.props.setActiveTab(value, this.props.navigate);
  };

  handleCloseSnackBar = () => {
    this.props.toggleSnackbar(false);
  };

  handleCloseErrorSnackBar = () => {
    this.props.toggleErrorSnackbar(false);
  };

  submitConfirmationDialog = () => {
    this.closeConfirmationDialog();
    this.setState({ acceptedConfirmationDialog: true });
    this.props.onRemoteSubmit();
  };

  closeConfirmationDialog = () => {
    this.setState({ openConfirmationDialog: false });
  };

  closeAutoGeneratedDialog = () => {
    this.submitForm(this.state.formData);
    this.setState({
      autoGeneratedConfirmationDialog: false,
      formData: null,
    })
  }

  submitAutoGeneratedDialog = () => {
    const { formData } = this.state
    formData.i2p2_state.system_generated_values = []
    this.submitForm(formData);
    this.setState({
      autoGeneratedConfirmationDialog: false,
      formData: null,
    })
  }

  handleMobileMenuOpen = () => {
    this.setState({ mobileMenuOpened: true });
  };

  handleMobileMenuClose = () => {
    this.setState({ mobileMenuOpened: false });
  };

  render() {
    const { config, showSuccesfullUpdateSnackBar, closeFirmwareUpdateSnackBar, modal } = this.props;

    const chargerUuid = this.state.currentChargerData && this.state.currentChargerData.c_uuid ? this.state.currentChargerData.c_uuid : null;

    const activeTab = get(this.props, 'match.params.tab') || this.state.activeTab;
    const ifReadOnly = this.state.formMode === 'view';
    const firmwareUpdateSuccessMessage = `FW Update command was successfully sent to the ${chargerUuid} charger.`;

    const hasPlaza = get(this.props, 'currentCharger.plaza_name') !== null;

    return (
      <div className="content-wrapper">
        <Snackbar open={this.props.showSnackbar} message="Data is saved." autoHideDuration={2000} onClose={this.handleCloseSnackBar} />
        <Snackbar open={this.props.showErrorSnackbar} message={this.props.error || ''} onClose={this.handleCloseErrorSnackBar} />
        <Snackbar open={showSuccesfullUpdateSnackBar} message={firmwareUpdateSuccessMessage} onClose={closeFirmwareUpdateSnackBar} />
        <ConfirmationDialog
          onSubmit={this.submitConfirmationDialog}
          onCancel={this.closeConfirmationDialog}
          submitLabel="Submit"
          cancelLabel="Cancel"
          open={this.state.openConfirmationDialog && !hasPlaza}
          bodyText="Are you sure you want to inactivate this charger?"
        />
        <ConfirmationDialog
          onSubmit={this.closeConfirmationDialog}
          submitLabel="OK"
          open={this.state.openConfirmationDialog && hasPlaza}
          bodyText="To inactivate this charger you need to remove it from the plaza first."
          nonCancellable
        />
        <ConfirmationDialog
          onSubmit={this.submitAutoGeneratedDialog}
          onCancel={this.closeAutoGeneratedDialog}
          submitLabel="Confirm"
          cancelLabel="Remind later"
          open={this.state.autoGeneratedConfirmationDialog}
          bodyText={'Some fields are marked as "System Generated" and have not been modified. Please confirm if the current values are accurate.'}
        />
        <ChargerDetailsForm
          config={config}
          ifModal={modal}
          updateTimestamp={this.state.updateTimestamp}
          updateReason={this.state.updateReason}
          currentChargerData={this.state.currentChargerData}
          readOnly={ifReadOnly}
          onClose={this.onClose}
          data={this.state.tabsContent}
          activeTabValue={activeTab}
          selectTab={this.selectTab}
          onSubmit={this.onSubmit}
          onChargerDetailsTabExport={this.props.onChargerDetailsTabExport}
          onSubmitFail={this.scrollToFirstError}
          showExitDialog={this.state.showExitDialog}
          backButtonHandler={this.backButtonHandler}
          isMobileMenuOpened={this.state.mobileMenuOpened}
          onMobileMenuOpen={this.handleMobileMenuOpen}
          onMobileMenuClose={this.handleMobileMenuClose}
          {...this.props}
        />
      </div>
    );
  }
}

export default ChargerDetails;
